import { c as _c } from "react/compiler-runtime";
// Каркас личного кабинета покупателя по макету «Профиль» (Figma ЛК Покупателя):
// слева колонка 270px — карточка пользователя (#F8F8F8 r20 p16: аватар 67, имя
// 16/20 w600, ссылка «Личные данные» 14/15 w600 #0088FF) и меню разделов через
// 10 (пункт 56 r16, p16/12, иконка 24 + 10 + подпись 16/18 w600, активный —
// заливка #F8F8F8); справа — серая панель раздела (#F8F8F8 r20 p20, 850px в
// кадре 1200, заголовок 20/23 w600).
// Разделы — вложенные роуты /profile/*, каждый рендерится в <Outlet />.
//
// Телефон (до 767 px, кадры «Профиль» шириной 420, 4118:*): меню — отдельный
// экран-список. /profile — карточка пользователя с крестиком и пункты меню
// строками на всю ширину; раздел — карточка с «бургером» (возврат к меню) и
// панель раздела под ней. Планшет и десктоп — как были (колонка слева).
import { ChevronRight, FileSearch, Layers, Pencil } from "lucide-react";
import { Fragment, useEffect, useSyncExternalStore } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useNotificationCount } from "@/api/use-notifications";
import { BellIcon, CardIcon, EyeIcon, type IconProps, InfoSquareIcon, LogoutIcon, RefreshSquareIcon, ShoppingBag2Icon, ThumbUpIcon, TicketIcon, WorkIcon, XIcon } from "@/components/icons";
import { Segmented } from "@/components/ui/segmented";
import { cn } from "@/lib/utils";
import { useProfile, useWishlist } from "@/store";
import { SignInCard } from "./sign-in-card";
import { InstallAppPrompt } from "@/components/pwa/install-prompt";

// Иконок «Запросы» и «Образы» в наборе макета нет — контур lucide той же
// толщины (1,5), что у неактивной «Главной» в таб-баре.
function RequestsNavIcon(t0) {
  const $ = _c(3);
  const {
    size: t1,
    className
  } = t0;
  const size = t1 === undefined ? 24 : t1;
  let t2;
  if ($[0] !== className || $[1] !== size) {
    t2 = <FileSearch size={size} strokeWidth={1.5} className={className} aria-hidden="true" />;
    $[0] = className;
    $[1] = size;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function LooksNavIcon(t0) {
  const $ = _c(3);
  const {
    size: t1,
    className
  } = t0;
  const size = t1 === undefined ? 24 : t1;
  let t2;
  if ($[0] !== className || $[1] !== size) {
    t2 = <Layers size={size} strokeWidth={1.5} className={className} aria-hidden="true" />;
    $[0] = className;
    $[1] = size;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<IconProps>;
  badge?: "notifications";
};

// Меню — ровно по макету. Разделы вне кадров (Баланс, Адреса, Устройства)
// живут на своих роутах и открываются ссылками из «Способов оплаты» и
// «Личных данных», в меню их нет.

// Иконки — набор макета (DS): shopping-bag 2, Outline/Notification, eye,
// thumb-up, Outline/refresh-square-2, Outline/card, Outline/Info Square, logout 6.
const NAV: NavItem[] = [{
  to: "/profile/orders",
  label: "Мои заказы",
  icon: ShoppingBag2Icon
},
// fd-request-21: вход в «Запросы на поиск и изготовление» (страница /requests).
{
  to: "/requests",
  label: "Запросы",
  icon: RequestsNavIcon
}, {
  to: "/profile/notifications",
  label: "Уведомления",
  icon: BellIcon,
  badge: "notifications"
}, {
  to: "/profile/history",
  label: "История просмотра",
  icon: EyeIcon
},
// fd-discovery-04: личные подборки покупателя.
{
  to: "/profile/looks",
  label: "Мои образы",
  icon: LooksNavIcon
}, {
  to: "/profile/reviews",
  label: "Отзывы",
  icon: ThumbUpIcon
}, {
  to: "/profile/returns",
  label: "Возвраты",
  icon: RefreshSquareIcon
}, {
  to: "/profile/payments",
  label: "Способы оплаты",
  icon: CardIcon
},
// fd-promo-34: «Мои купоны» — сверх макета «Профиль».
{
  to: "/profile/coupons",
  label: "Мои промокоды",
  icon: TicketIcon
}, {
  to: "/profile/support",
  label: "Поддержка",
  icon: InfoSquareIcon
},
// Единый партнёрский кабинет (UGC-креаторы, арбитражники) - тот же вход, свой раздел /partner.
{
  to: "/partner",
  label: "Партнёрам",
  icon: WorkIcon
}];
export function ProfileShell() {
  const $ = _c(14);
  const token = useProfile(_temp);
  const user = useProfile(_temp2);
  const profile = useProfile(_temp3);
  const hydrateMe = useProfile(_temp4);
  const syncWishlist = useWishlist(_temp5);
  let t0;
  let t1;
  if ($[0] !== hydrateMe || $[1] !== token || $[2] !== user) {
    t0 = () => {
      if (token && !user) {
        hydrateMe().catch(_temp6);
      }
    };
    t1 = [hydrateMe, token, user];
    $[0] = hydrateMe;
    $[1] = token;
    $[2] = user;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  const isMobile = useIsProfileMobile();
  const location = useLocation();
  if (!user || !profile) {
    let t2;
    if ($[5] !== syncWishlist) {
      t2 = <SignInCard onSignedIn={() => void syncWishlist()} />;
      $[5] = syncWishlist;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  if (isMobile) {
    let t2;
    if ($[7] !== location.pathname || $[8] !== location.search) {
      t2 = /^\/profile\/?$/.test(location.pathname) && !new URLSearchParams(location.search).has("tab");
      $[7] = location.pathname;
      $[8] = location.search;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    const menuOpen = t2;
    let t3;
    if ($[10] !== menuOpen) {
      t3 = <div className="-mx-1 flex flex-col gap-3 pt-0.5" data-testid="profile-mobile"><ProfileUserCard compact={true} action={menuOpen ? <MenuCloseButton /> : <MenuOpenButton />} />{menuOpen ? <ProfileMobileMenu /> : <div className="min-w-0"><Outlet /></div>}</div>;
      $[10] = menuOpen;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  }
  let t2;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ProfileSidebar />;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="mx-auto grid w-full max-w-[1140px] items-start gap-5 pt-2.5 lg:grid-cols-[270px_minmax(0,1fr)]">{t2}<div className="min-w-0"><Outlet /></div></div>;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
function _temp6() {}
function _temp5(s_3) {
  return s_3.syncFromServer;
}
function _temp4(s_2) {
  return s_2.hydrateMe;
}
function _temp3(s_1) {
  return s_1.profile;
}
function _temp2(s_0) {
  return s_0.user;
}
function _temp(s) {
  return s.token;
}
function ProfileSidebar() {
  const $ = _c(15);
  const navigate = useNavigate();
  const signOut = useProfile(_temp7);
  const unread = useNotificationCount();
  const unreadCount = unread.data?.count ?? 0;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ProfileUserCard />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== unreadCount) {
    t1 = NAV.map(item => <Fragment key={item.to}><SidebarLink item={item} count={item.badge === "notifications" ? unreadCount : 0} /></Fragment>);
    $[1] = unreadCount;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== navigate || $[4] !== signOut) {
    t2 = () => {
      signOut().then(() => navigate("/profile"));
    };
    $[3] = navigate;
    $[4] = signOut;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <LogoutIcon size={24} className="shrink-0" />;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2) {
    t4 = <button type="button" onClick={t2} className="flex h-14 items-center gap-2.5 self-start rounded-[16px] px-3 text-[16px] leading-[18px] font-semibold tracking-[-0.32px] text-[#161616] outline-none transition-colors hover:bg-[#F8F8F8] focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">{t3}Выйти из аккаунта</button>;
    $[7] = t2;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <nav className="flex flex-row flex-wrap gap-2.5 lg:flex-col" aria-label={"\u0420\u0430\u0437\u0434\u0435\u043B\u044B \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430"}>{t1}{t4}</nav>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <InstallAppPrompt placement="profile" />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5) {
    t7 = <aside className="flex flex-col gap-2.5">{t0}{t5}{t6}</aside>;
    $[13] = t5;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}

// Телефон — по брейкпоинту md Tailwind (768): ниже него кадры 420.
function _temp7(s) {
  return s.signOut;
}
const MOBILE_QUERY = "(max-width: 767px)";
const subscribeMobile = (onChange: () => void) => {
  const mq = window.matchMedia(MOBILE_QUERY);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
};
export function useIsProfileMobile() {
  return useSyncExternalStore(subscribeMobile, _temp8, _temp9);
}
function _temp9() {
  return false;
}
function _temp8() {
  return window.matchMedia(MOBILE_QUERY).matches;
}
function useFullName(): string {
  const profile = useProfile(s => s.profile);
  const user = useProfile(s_0 => s_0.user);
  return [profile?.firstName, profile?.lastName].filter(Boolean).join(" ").trim() || profile?.name || user?.phone || "";
}

// Карточка пользователя #F8F8F8 r20. Десктоп: p16, аватар 67. Телефон
// (кадры 420): p16/12, аватар 50, справа иконка 24 — «бургер» или крестик.
function ProfileUserCard(t0) {
  const $ = _c(21);
  const {
    compact: t1,
    action
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const profile = useProfile(_temp10);
  const fullName = useFullName();
  const t2 = compact ? "px-3 py-4" : "p-4";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("flex items-center gap-2.5 rounded-[20px] bg-[#F8F8F8]", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = profile?.avatarUrl;
  const t5 = compact ? 50 : 67;
  let t6;
  if ($[2] !== fullName || $[3] !== t4 || $[4] !== t5) {
    t6 = <Avatar url={t4} name={fullName} size={t5} />;
    $[2] = fullName;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = compact ? "gap-1" : "gap-1.5";
  let t8;
  if ($[6] !== t7) {
    t8 = cn("flex min-w-0 flex-1 flex-col", t7);
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== fullName) {
    t9 = <p className="line-clamp-2 break-words text-[16px] leading-5 font-semibold tracking-[-0.31px] text-[#161616]">{fullName}</p>;
    $[8] = fullName;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Link to="/profile/personal" className="self-start rounded-sm text-[14px] leading-[15px] font-semibold tracking-[-0.28px] text-[#0088FF] outline-none hover:underline focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">Личные данные</Link>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== t8 || $[12] !== t9) {
    t11 = <div className={t8}>{t9}{t10}</div>;
    $[11] = t8;
    $[12] = t9;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== action) {
    t12 = action ? <div className="flex shrink-0 self-start">{action}</div> : null;
    $[14] = action;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t11 || $[17] !== t12 || $[18] !== t3 || $[19] !== t6) {
    t13 = <div className={t3}>{t6}{t11}{t12}</div>;
    $[16] = t11;
    $[17] = t12;
    $[18] = t3;
    $[19] = t6;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  return t13;
}

// Откуда открыли меню — туда вернёт крестик (и подсветит этот пункт).
function _temp10(s) {
  return s.profile;
}
type MenuState = {
  from?: string;
} | null;
const iconButton = "grid size-6 place-items-center rounded-sm text-black outline-none focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40";

// «Бургер» (иконка menu 6 · 24 из DS: три линии, нижняя короче) — назад к меню.
function MenuOpenButton() {
  const $ = _c(5);
  const location = useLocation();
  const t0 = `${location.pathname}${location.search}`;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      from: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = t1 satisfies MenuState;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <svg width={24} height={24} viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 6h18M3 12h18M10 18h11" stroke="currentColor" strokeWidth={2} strokeLinecap="round" /></svg>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t2) {
    t4 = <Link to="/profile" state={t2} aria-label={"\u041C\u0435\u043D\u044E \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430"} data-testid="profile-menu-open" className={iconButton}>{t3}</Link>;
    $[3] = t2;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}

// Крестик закрывает меню: шаг назад, если пришли из раздела, иначе — в заказы.
function MenuCloseButton() {
  const $ = _c(6);
  const navigate = useNavigate();
  const from = (useLocation().state as MenuState)?.from;
  let t0;
  if ($[0] !== from || $[1] !== navigate) {
    t0 = () => from ? navigate(-1) : navigate("/profile/orders", {
      replace: true
    });
    $[0] = from;
    $[1] = navigate;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <XIcon size={24} />;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0) {
    t2 = <button type="button" aria-label={"\u0417\u0430\u043A\u0440\u044B\u0442\u044C \u043C\u0435\u043D\u044E"} data-testid="profile-menu-close" onClick={t0} className={iconButton}>{t1}</button>;
    $[4] = t0;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}

// Экран меню на телефоне (кадр 4118:42537): те же пункты NAV строками на всю
// ширину — h48, p12, r16, иконка 24 + 10 + подпись 16/18 w600, через 10;
// пункт раздела, из которого пришли, — с заливкой #F8F8F8.
function ProfileMobileMenu() {
  const $ = _c(14);
  const navigate = useNavigate();
  const signOut = useProfile(_temp11);
  const unread = useNotificationCount();
  const unreadCount = unread.data?.count ?? 0;
  const from = (useLocation().state as MenuState)?.from ?? "";
  let t0;
  if ($[0] !== from || $[1] !== unreadCount) {
    t0 = NAV.map(item => {
      const Icon = item.icon;
      const active = from === item.to || from.startsWith(`${item.to}/`) || from.startsWith(`${item.to}?`);
      const count = item.badge === "notifications" ? unreadCount : 0;
      return <Link key={item.to} to={item.to} className={cn("flex h-12 w-full items-center gap-2.5 rounded-[16px] px-3 text-left text-[16px] leading-[18px] font-semibold tracking-[-0.32px] text-[#161616] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40", active ? "bg-[#F8F8F8]" : "active:bg-[#F8F8F8]/70")}><Icon size={24} className="shrink-0" /><span className="min-w-0 truncate">{item.label}</span>{count > 0 && <CountBadge count={count} />}</Link>;
    });
    $[0] = from;
    $[1] = unreadCount;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== navigate || $[4] !== signOut) {
    t1 = () => {
      signOut().then(() => navigate("/profile"));
    };
    $[3] = navigate;
    $[4] = signOut;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("flex h-12 w-full items-center gap-2.5 rounded-[16px] px-3 text-left text-[16px] leading-[18px] font-semibold tracking-[-0.32px] text-[#161616] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40", "active:bg-[#F8F8F8]/70");
    t3 = <LogoutIcon size={24} className="shrink-0" />;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1) {
    t4 = <button type="button" onClick={t1} className={t2}>{t3}Выйти из аккаунта</button>;
    $[8] = t1;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <InstallAppPrompt placement="profile" />;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t0 || $[12] !== t4) {
    t6 = <nav className="flex flex-col gap-2.5" aria-label={"\u0420\u0430\u0437\u0434\u0435\u043B\u044B \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430"} data-testid="profile-mobile-menu">{t0}{t4}{t5}</nav>;
    $[11] = t0;
    $[12] = t4;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function _temp11(s) {
  return s.signOut;
}
function CountBadge(t0) {
  const $ = _c(2);
  const {
    count
  } = t0;
  const t1 = count > 99 ? "99+" : count;
  let t2;
  if ($[0] !== t1) {
    t2 = <span className="grid h-5 min-w-5 shrink-0 place-items-center rounded-full bg-[#E1574F] px-0.5 text-[12px] leading-4 font-medium tracking-normal text-white">{t1}</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}
function SidebarLink(t0) {
  const $ = _c(11);
  const {
    item,
    count
  } = t0;
  const Icon = item.icon;
  let t1;
  if ($[0] !== Icon) {
    t1 = <Icon size={24} className="shrink-0" />;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== item.label) {
    t2 = <span>{item.label}</span>;
    $[2] = item.label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== count) {
    t3 = count > 0 && <CountBadge count={count} />;
    $[4] = count;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== item.to || $[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <NavLink to={item.to} className={_temp12}>{t1}{t2}{t3}</NavLink>;
    $[6] = item.to;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function _temp12(t0) {
  const {
    isActive
  } = t0;
  return cn("flex h-14 items-center gap-2.5 self-start rounded-[16px] px-3 text-[16px] leading-[18px] font-semibold tracking-[-0.32px] text-[#161616] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40", isActive ? "bg-[#F8F8F8]" : "hover:bg-[#F8F8F8]/70");
}
export function Avatar(t0) {
  const $ = _c(10);
  const {
    url,
    name,
    size: t1
  } = t0;
  const size = t1 === undefined ? 67 : t1;
  let t2;
  if ($[0] !== name) {
    t2 = name.trim().slice(0, 1).toUpperCase();
    $[0] = name;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const initial = t2;
  let t3;
  if ($[2] !== size) {
    t3 = {
      width: size,
      height: size
    };
    $[2] = size;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== initial || $[5] !== url) {
    t4 = url ? <img src={url} alt="" className="size-full object-cover" /> : initial;
    $[4] = initial;
    $[5] = url;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <span className="grid shrink-0 place-items-center overflow-hidden rounded-full bg-[#EDEDED] text-[22px] font-medium text-[#8A8A8A]" style={t3}>{t4}</span>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}

// Панель раздела: серый фон #F8F8F8 r20, отступ 20 (карточки внутри — 810 из
// 850), заголовок 20px. Телефон (кадры 420): отступ 16/12, заголовок и крошки
// 16/20, до содержимого 12, высота по содержимому. `crumbs` — хлебные крошки «Мои заказы › Заказ №…»:
// все звенья кроме последнего — ссылки, разделитель — шеврон.
export function ProfilePanel(t0) {
  const $ = _c(14);
  const {
    title,
    crumbs,
    actions,
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex flex-col rounded-[20px] bg-[#F8F8F8] px-3 py-4 md:min-h-[520px] md:p-5", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== crumbs || $[3] !== title) {
    t2 = crumbs ? <nav className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[16px] leading-5 font-semibold tracking-[-0.31px] text-[#161616] md:gap-x-3 md:text-[20px] md:leading-[23px]" aria-label={"\u041D\u0430\u0432\u0438\u0433\u0430\u0446\u0438\u044F"}>{crumbs.map(_temp13)}</nav> : <h1 className="text-[16px] leading-5 font-semibold tracking-[-0.31px] text-[#161616] md:text-[20px] md:leading-[23px]">{title}</h1>;
    $[2] = crumbs;
    $[3] = title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== actions) {
    t3 = actions ? <div className="flex shrink-0 items-center gap-3">{actions}</div> : null;
    $[5] = actions;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <header className="mb-3 flex min-h-5 items-center justify-between gap-3 md:mb-4 md:min-h-[23px]">{t2}{t3}</header>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== children || $[11] !== t1 || $[12] !== t4) {
    t5 = <section className={t1}>{t4}{children}</section>;
    $[10] = children;
    $[11] = t1;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}

// Сегмент-переключатель вкладок раздела («Актуальные заказы | Завершенные»,
// «Ждут отзыва | Мои отзывы», «Активные | Открыт спор | Архив») — компонент DS
// `Segmented` (подложка h40 r96 #1F1F1F 3 %, активный h36 #1F1F1F с тенью,
// 14/18 w500) с отступом 16 до содержимого раздела.
function _temp13(c, i) {
  return <Fragment key={`${c.label}-${i}`}>{i > 0 && <ChevronRight className="size-5 shrink-0" strokeWidth={1.5} aria-hidden="true" />}{c.to ? <Link to={c.to} className="rounded-sm outline-none hover:text-osmix-tangelo focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">{c.label}</Link> : <span>{c.label}</span>}</Fragment>;
}
export function ProfileSegments(t0) {
  const $ = _c(8);
  const {
    value,
    onChange,
    options,
    equal: t1,
    className
  } = t0;
  const equal = t1 === undefined ? false : t1;
  const isMobile = useIsProfileMobile();
  const t2 = equal && !isMobile;
  let t3;
  if ($[0] !== className) {
    t3 = cn("mb-3 max-w-full shrink-0 self-start overflow-x-auto [scrollbar-width:none] md:mb-4", className);
    $[0] = className;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== onChange || $[3] !== options || $[4] !== t2 || $[5] !== t3 || $[6] !== value) {
    t4 = <Segmented value={value} onValueChange={onChange} options={options} block={t2} className={t3} />;
    $[2] = onChange;
    $[3] = options;
    $[4] = t2;
    $[5] = t3;
    $[6] = value;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}

// Заглушка раздела, который ещё собирается (рендерится внутри панели).
export function ProfileSectionPending(t0) {
  const $ = _c(3);
  const {
    title
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex flex-1 items-center justify-center rounded-[20px] bg-white p-10 text-[14px] text-[#8A8A8A]"><Pencil className="mr-2 size-4" /> Раздел собирается</div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== title) {
    t2 = <ProfilePanel title={title}>{t1}</ProfilePanel>;
    $[1] = title;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}